/**
 * The setup wizard's steps. Each receives { org, reload, notify, markDone } and
 * saves through /api/org/me. `markDone()` records the step on the organization;
 * the shell shows it as completed and advances.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Grid, IconButton, InputAdornment,
  Link, List, ListItem, ListItemText, MenuItem, Paper, Rating, Stack, Tab, Tabs, TextField, Typography
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon, OpenInNew as OpenIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import axios from 'axios';
import LivePreview from './LivePreview';
import { SITE_DEFAULTS, withName } from './sitePageDefaults';
import { putMe, uploadImage, getDocuments, getDocument, publishDocument, getTeam, errorMessage, getStripe, startStripeOnboarding, stripeGoLive, stripeBackToDemo } from './wizardApi';
import { useOrg } from '../../context/OrgContext';
import CustomerApp from '../../owner/CustomerApp';
import UnlockDialog from '../../owner/UnlockDialog';
import CreateUserDialog from '../../settings/components/CreateUserDialog';
import { useUser } from '../../context/UserContext';
import { getOwnerToken, getOwnerExpiresAt, isOwnerLockedError } from '../../utils/ownerSession';

const API = process.env.REACT_APP_API_URL || '';

const Section = ({ title, blurb, children, action }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: blurb ? 0.5 : 2 }}>
      <Typography variant="h6">{title}</Typography>
      {action}
    </Box>
    {blurb && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{blurb}</Typography>}
    {children}
  </Paper>
);

const SaveBar = ({ saving, onSave, label = 'Save and continue', disabled, secondary }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
    {secondary}
    <Button variant="contained" onClick={onSave} disabled={saving || disabled}>{saving ? 'Saving…' : label}</Button>
  </Box>
);

// ---- 1. Branding -------------------------------------------------------------------------------

export function StepBranding({ org, reload, notify, markDone }) {
  const { refresh } = useOrg();
  const emptyContacts = { customer: { email: '', phone: '' }, provider: { email: '', phone: '' }, applicant: { email: '', phone: '' } };
  const [form, setForm] = useState({ name: '', legalName: '', primary: '#1976d2', secondary: '#dc004e', contacts: emptyContacts });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!org) return;
    const c = org.branding.contacts || {};
    setForm({
      name: org.branding.name || '', legalName: org.branding.legalName || '',
      primary: org.branding.colors?.primary || '#1976d2', secondary: org.branding.colors?.secondary || '#dc004e',
      contacts: {
        customer: { email: c.customer?.email || '', phone: c.customer?.phone || '' },
        provider: { email: c.provider?.email || '', phone: c.provider?.phone || '' },
        applicant: { email: c.applicant?.email || '', phone: c.applicant?.phone || '' }
      }
    });
  }, [org]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const updateContact = (audience, key) => (e) => setForm((f) => ({ ...f, contacts: { ...f.contacts, [audience]: { ...f.contacts[audience], [key]: e.target.value } } }));
  const hex = (c) => /^#[0-9a-fA-F]{6}$/.test(c);
  const emailOk = (v) => !v || /^\S+@\S+\.\S+$/.test(v);
  const phoneOk = (v) => !v || v.replace(/\D/g, '').length >= 10;
  const contactsComplete = ['customer', 'provider'].every((a) => form.contacts[a].email && form.contacts[a].phone);

  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadImage(file, kind);
      await reload();
      refresh();
      notify(kind === 'icon' ? 'App icon uploaded' : kind === 'hero' ? 'Home-page photo uploaded' : 'Logo uploaded', 'success');
    } catch (err) {
      notify(errorMessage(err, 'Could not upload the image'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (form.name.trim().length < 2) return notify('Enter your company name', 'error');
    if (!hex(form.primary) || !hex(form.secondary)) return notify('Colors must be hex values like #1976d2', 'error');
    for (const a of ['customer', 'provider', 'applicant']) {
      if (!emailOk(form.contacts[a].email)) return notify(`The ${a} contact email is not valid`, 'error');
      if (!phoneOk(form.contacts[a].phone)) return notify(`The ${a} contact phone needs 10 digits`, 'error');
    }
    setSaving(true);
    try {
      await putMe({ branding: { name: form.name, legalName: form.legalName, colors: { primary: form.primary, secondary: form.secondary }, contacts: form.contacts } });
      await reload();
      refresh();
      if (!org.branding.logoUrl) { notify('Saved. Upload your logo to complete this step.', 'info'); return; }
      if (!contactsComplete) { notify('Saved. Enter the customer and service-provider contact email and phone to complete this step.', 'info'); return; }
      await markDone('branding');
    } catch (err) {
      notify(errorMessage(err, 'Could not save'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Section title="Company name and colors" blurb="Your name appears on the portal, the customer site, emails, texts and agreements. Colors set the look of every page.">
          <Stack spacing={2}>
            <TextField label="Company name" value={form.name} onChange={update('name')} required inputProps={{ maxLength: 80 }} />
            <TextField label="Legal name" value={form.legalName} onChange={update('legalName')} helperText="As it should appear on agreements and receipts, e.g. Acme Roadside LLC" inputProps={{ maxLength: 120 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="Primary color" value={form.primary} onChange={update('primary')} error={!hex(form.primary)}
                InputProps={{ startAdornment: <InputAdornment position="start"><input type="color" value={hex(form.primary) ? form.primary : '#1976d2'} onChange={update('primary')} style={{ width: 28, height: 28, border: 0, background: 'none', padding: 0 }} /></InputAdornment> }} />
              <TextField label="Secondary color" value={form.secondary} onChange={update('secondary')} error={!hex(form.secondary)}
                InputProps={{ startAdornment: <InputAdornment position="start"><input type="color" value={hex(form.secondary) ? form.secondary : '#dc004e'} onChange={update('secondary')} style={{ width: 28, height: 28, border: 0, background: 'none', padding: 0 }} /></InputAdornment> }} />
            </Box>
          </Stack>
        </Section>
        <Section title="Logo and app icon" blurb="PNG, JPEG or WebP. The logo goes on the portal header, customer site, emails and PDFs; the icon is used for the browser tab and the app's home-screen shortcut (square, at least 512 px).">
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Box sx={{ width: 180, height: 90, border: '1px dashed', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, bgcolor: '#fafafa' }}>
                {org?.branding.logoUrl ? <Box component="img" src={org.branding.logoUrl} alt="Logo" sx={{ maxWidth: 170, maxHeight: 80, objectFit: 'contain' }} /> : <Typography variant="caption" color="text.secondary">No logo yet</Typography>}
              </Box>
              <Button component="label" size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploading}>{org?.branding.logoUrl ? 'Replace logo' : 'Upload logo'}<input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={upload('logo')} /></Button>
            </Box>
            <Box>
              <Box sx={{ width: 90, height: 90, border: '1px dashed', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, bgcolor: '#fafafa' }}>
                {org?.branding.iconUrl ? <Box component="img" src={org.branding.iconUrl} alt="Icon" sx={{ width: 80, height: 80, objectFit: 'contain' }} /> : <Typography variant="caption" color="text.secondary" align="center">Optional</Typography>}
              </Box>
              <Button component="label" size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploading}>{org?.branding.iconUrl ? 'Replace icon' : 'Upload icon'}<input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={upload('icon')} /></Button>
            </Box>
          </Box>
        </Section>
        <Section title="Home-page photo" blurb="The large background photo at the top of your customer website. A wide landscape photo works best (at least 1600 × 900, JPEG or WebP). Large photos are shrunk automatically. Without one, the standard roadside photo is used.">
          <Box sx={{ width: '100%', maxWidth: 420, aspectRatio: '16 / 9', border: '1px dashed', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, bgcolor: '#fafafa', overflow: 'hidden' }}>
            {org?.branding.heroImageUrl ? <Box component="img" src={org.branding.heroImageUrl} alt="Home-page photo" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Typography variant="caption" color="text.secondary">Standard photo in use</Typography>}
          </Box>
          <Button component="label" size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploading}>{org?.branding.heroImageUrl ? 'Replace photo' : 'Upload photo'}<input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={upload('hero')} /></Button>
        </Section>
        <Section title="Contact information" blurb="Who people reach. Your own login email and phone are never shown anywhere. Customer and service-provider contacts are required; applicants fall back to the customer contact if left blank.">
          <Stack spacing={2.5}>
            {[
              ['customer', 'Customers', 'Shown on your customer website and app, on receipts and customer documents, and used as the reply-to on customer emails and texts.'],
              ['provider', 'Service providers', 'Shown in the portal, on rate agreements and provider documents, and used as the reply-to on provider emails.'],
              ['applicant', 'Applicants', 'Shown on the provider application form, your careers pages and in hiring emails. Falls back to the service-provider contact if left blank.']
            ].map(([audience, label, hint]) => (
              <Box key={audience}>
                <Typography variant="subtitle2">{label}{audience !== 'applicant' ? ' *' : ''}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{hint}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField size="small" label="Email" type="email" value={form.contacts[audience].email} onChange={updateContact(audience, 'email')} error={!emailOk(form.contacts[audience].email)} />
                  <TextField size="small" label="Phone" value={form.contacts[audience].phone} onChange={updateContact(audience, 'phone')} error={!phoneOk(form.contacts[audience].phone)} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Section>
        <SaveBar saving={saving} onSave={save} />
      </Grid>
      <Grid item xs={12} md={5}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Live preview</Typography>
        <LivePreview name={form.name} logoUrl={org?.branding.logoUrl} primary={form.primary} secondary={form.secondary} tagline={org?.content?.tagline} />
      </Grid>
    </Grid>
  );
}

// ---- 2. About and reviews ----------------------------------------------------------------------

const DEFAULT_FEATURES = [
  { title: 'Steady Job Volume', text: 'We dispatch calls from our customers straight to your company — towing, jump starts, tire changes, lockouts, winch-outs, and more, in the territories you choose.' },
  { title: 'Weekly Pay + On-Time Bonuses', text: 'Completed jobs pay out by ACH every week with an itemized statement. Arrive early or on time and earn a cash bonus on top of your rate.' },
  { title: 'Modern Dispatch, No Paperwork Pile', text: 'Job offers hit your drivers\' phones through our app: accept with an ETA in two taps, navigate, upload photos, capture signatures, and track everything live.' }
];

function ImageSlot({ label, url, kind, onUpload, uploading, wide }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{label}</Typography>
      <Box sx={{ width: '100%', maxWidth: wide ? 420 : 220, aspectRatio: wide ? '16 / 9' : '4 / 3', border: '1px dashed', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, bgcolor: '#fafafa', overflow: 'hidden' }}>
        {url ? <Box component="img" src={url} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Typography variant="caption" color="text.secondary">Standard photo in use</Typography>}
      </Box>
      <Button component="label" size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploading}>{url ? 'Replace' : 'Upload'}<input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload(kind)} /></Button>
    </Box>
  );
}

export function StepAbout({ org, reload, notify, markDone }) {
  const [form, setForm] = useState({ tagline: '', heroText: '', about: '', reviews: [], providerPage: { ctaTitle: '', ctaText: '', features: [] } });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (!org) return;
    const pp = org.content.providerPage || {};
    const features = (pp.features || []).length ? pp.features.map((f) => ({ title: f.title || '', text: f.text || '', imageUrl: f.imageUrl || null })) : DEFAULT_FEATURES.map((f) => ({ ...f, imageUrl: null }));
    setForm({ tagline: org.content.tagline || '', heroText: org.content.heroText || '', about: org.content.about || '', reviews: org.content.reviews || [], providerPage: { ctaTitle: pp.ctaTitle || '', ctaText: pp.ctaText || '', features } });
  }, [org]);
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const updatePage = (key) => (e) => setForm((f) => ({ ...f, providerPage: { ...f.providerPage, [key]: e.target.value } }));
  const setFeature = (i, patch) => setForm((f) => ({ ...f, providerPage: { ...f.providerPage, features: f.providerPage.features.map((x, j) => (j === i ? { ...x, ...patch } : x)) } }));
  const setReview = (i, patch) => setForm((f) => ({ ...f, reviews: f.reviews.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      // Save the card text first so a new card exists to hold its photo
      await putMe({ content: { providerPage: { features: form.providerPage.features.map(({ title, text }) => ({ title, text })) } } });
      await uploadImage(file, kind);
      await reload();
      notify('Photo uploaded', 'success');
    } catch (err) {
      notify(errorMessage(err, 'Could not upload the photo'), 'error');
    } finally {
      setUploading(false);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      await putMe({ content: { ...form, providerPage: { ctaTitle: form.providerPage.ctaTitle, ctaText: form.providerPage.ctaText, features: form.providerPage.features.map(({ title, text }) => ({ title, text })) } } });
      await reload();
      await markDone('about');
    } catch (err) {
      notify(errorMessage(err, 'Could not save'), 'error');
    } finally {
      setSaving(false);
    }
  };
  const pp = org?.content?.providerPage || {};
  return (
    <>
      <Section title="About your company" blurb="This text appears on your customer website's home and About pages and on the provider application form.">
        <Stack spacing={2}>
          <TextField label="Tagline" value={form.tagline} onChange={update('tagline')} inputProps={{ maxLength: 120 }} helperText="One line under your name, e.g. “Fast, friendly roadside help across North Texas.”" />
          <TextField label="Home page headline text" value={form.heroText} onChange={update('heroText')} multiline minRows={2} inputProps={{ maxLength: 300 }} helperText="A sentence or two for the top of the home page" />
          <TextField label="About page" value={form.about} onChange={update('about')} multiline minRows={6} inputProps={{ maxLength: 5000 }} helperText="Who you are, where you operate, what makes you different" />
        </Stack>
      </Section>
      <Section title="Provider application page" blurb="The page where towing and service companies apply to work with you (your portal address + /applicationform). Edit the three feature cards and the call to action, and replace the photos.">
        <Stack spacing={2.5}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <ImageSlot label="Top photo" url={pp.heroImageUrl} kind="providerHero" onUpload={upload} uploading={uploading} wide />
            <ImageSlot label="About photo" url={pp.aboutImageUrl} kind="providerAbout" onUpload={upload} uploading={uploading} />
            <ImageSlot label="Call-to-action background" url={pp.ctaImageUrl} kind="providerCta" onUpload={upload} uploading={uploading} />
          </Box>
          <Typography variant="subtitle2">Feature cards</Typography>
          <Grid container spacing={2}>
            {form.providerPage.features.map((f, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                  <ImageSlot label={`Card ${i + 1} photo`} url={f.imageUrl} kind={`feature${i + 1}`} onUpload={upload} uploading={uploading} />
                  <TextField fullWidth size="small" label="Title" value={f.title} onChange={(e) => setFeature(i, { title: e.target.value })} inputProps={{ maxLength: 80 }} sx={{ mt: 1.5 }} />
                  <TextField fullWidth size="small" label="Text" value={f.text} onChange={(e) => setFeature(i, { text: e.target.value })} multiline minRows={3} inputProps={{ maxLength: 400 }} sx={{ mt: 1.5 }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
          <TextField label="Call-to-action title" value={form.providerPage.ctaTitle} onChange={updatePage('ctaTitle')} inputProps={{ maxLength: 120 }} placeholder="Apply in About 15 Minutes" />
          <TextField label="Call-to-action text" value={form.providerPage.ctaText} onChange={updatePage('ctaText')} multiline minRows={2} inputProps={{ maxLength: 400 }} placeholder="Tell us about your company, upload your W-9 and insurance, and pick your coverage ZIP codes…" />
        </Stack>
      </Section>
      <Section title="Customer reviews" blurb="Up to 12 reviews to show on your website. Use real reviews customers have given you permission to share."
        action={<Button size="small" startIcon={<AddIcon />} onClick={() => setForm((f) => ({ ...f, reviews: [...f.reviews, { name: '', rating: 5, text: '', source: '' }] }))} disabled={form.reviews.length >= 12}>Add review</Button>}>
        {form.reviews.length === 0 && <Typography variant="body2" color="text.secondary">No reviews yet. You can add them later.</Typography>}
        <Stack spacing={2}>
          {form.reviews.map((r, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <TextField size="small" label="Customer name" value={r.name} onChange={(e) => setReview(i, { name: e.target.value })} inputProps={{ maxLength: 60 }} />
                <Rating value={Number(r.rating) || 5} onChange={(e, v) => setReview(i, { rating: v || 5 })} />
                <TextField size="small" label="Source (Google, Yelp…)" value={r.source} onChange={(e) => setReview(i, { source: e.target.value })} inputProps={{ maxLength: 40 }} />
                <IconButton size="small" color="error" onClick={() => setForm((f) => ({ ...f, reviews: f.reviews.filter((_, j) => j !== i) }))}><DeleteIcon /></IconButton>
              </Box>
              <TextField fullWidth size="small" label="Review" value={r.text} onChange={(e) => setReview(i, { text: e.target.value })} multiline minRows={2} inputProps={{ maxLength: 600 }} />
            </Paper>
          ))}
        </Stack>
      </Section>
      <SaveBar saving={saving} onSave={save} />
    </>
  );
}

// ---- 2b. Site pages: the customer website's own wording --------------------------------------

const cardsOrDefaults = (saved, defaults) => (saved && saved.length ? saved.map((c) => ({ title: c.title || '', text: c.text || '' })) : defaults.map((c) => ({ ...c })));

export function StepSitePages({ org, reload, notify, markDone }) {
  const name = org?.branding?.name || org?.name || '';
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (!org) return;
    const sp = org.content?.sitePages || {};
    const d = withName(SITE_DEFAULTS, name);
    setForm({
      ctaTitle: sp.ctaTitle || d.ctaTitle,
      ctaText: sp.ctaText || d.ctaText,
      howTitle: sp.howTitle || d.howTitle,
      howLead: sp.howLead || d.howLead,
      steps: cardsOrDefaults(sp.steps, d.steps),
      benefits: cardsOrDefaults(sp.benefits, d.benefits),
      faq: (sp.faq && sp.faq.length ? sp.faq : d.faq).map((q) => ({ question: q.question || '', answer: q.answer || '' }))
    });
  }, [org, name]);
  if (!form) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setCard = (list, i, patch) => setForm((f) => ({ ...f, [list]: f[list].map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const setFaq = (i, patch) => setForm((f) => ({ ...f, faq: f.faq.map((q, j) => (j === i ? { ...q, ...patch } : q)) }));
  const reset = (key) => () => setForm((f) => ({ ...f, [key]: withName(SITE_DEFAULTS[key], name) }));
  const payload = () => ({ content: { sitePages: { ...form } } });

  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await putMe(payload());
      await uploadImage(file, kind);
      await reload();
      notify('Photo uploaded', 'success');
    } catch (err) {
      notify(errorMessage(err, 'Could not upload the photo'), 'error');
    } finally {
      setUploading(false);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      await putMe(payload());
      await reload();
      await markDone('sitePages');
    } catch (err) {
      notify(errorMessage(err, 'Could not save'), 'error');
    } finally {
      setSaving(false);
    }
  };
  const siteUrl = org?.urls?.customerSite;

  // A plain render function (not a component) so typing never remounts the fields
  const cardEditor = (list, label, cols = 6) => (
    <Grid container spacing={2}>
      {form[list].map((c, i) => (
        <Grid item xs={12} md={cols} key={i}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">{label} {i + 1}{!c.title && !c.text ? ' (hidden)' : ''}</Typography>
            <TextField fullWidth size="small" label="Title" value={c.title} onChange={(e) => setCard(list, i, { title: e.target.value })} inputProps={{ maxLength: 100 }} sx={{ mt: 1 }} />
            <TextField fullWidth size="small" label="Text" value={c.text} onChange={(e) => setCard(list, i, { text: e.target.value })} multiline minRows={2} inputProps={{ maxLength: list === 'benefits' ? 300 : 500 }} sx={{ mt: 1.5 }} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        This is the wording on your customer website{siteUrl ? <> (<Link href={siteUrl} target="_blank" rel="noopener">{siteUrl.replace(/^https?:\/\//, '')}</Link>)</> : ''}. Start from the standard text and change what you like. Clear both fields of a card to hide it. Service names, descriptions and prices are edited in the Services step.
      </Alert>
      <Section title="Call-to-action banner" blurb="The colored band near the bottom of every page, with the buttons that send customers to request service or join.">
        <Stack spacing={2}>
          <ImageSlot label="Background photo (optional)" url={org?.content?.sitePages?.ctaImageUrl} kind="siteCta" onUpload={upload} uploading={uploading} wide />
          <TextField label="Title" value={form.ctaTitle} onChange={set('ctaTitle')} inputProps={{ maxLength: 120 }} />
          <TextField label="Text" value={form.ctaText} onChange={set('ctaText')} multiline minRows={2} inputProps={{ maxLength: 300 }} />
        </Stack>
      </Section>
      <Section title="How it works" blurb="The four step cards on your home page and the How it works page."
        action={<Button size="small" onClick={() => { reset('steps')(); reset('howTitle')(); reset('howLead')(); }}>Use standard wording</Button>}>
        <Stack spacing={2}>
          <TextField label="How it works page: heading" value={form.howTitle} onChange={set('howTitle')} inputProps={{ maxLength: 120 }} />
          <TextField label="How it works page: intro" value={form.howLead} onChange={set('howLead')} multiline minRows={2} inputProps={{ maxLength: 400 }} />
          {cardEditor('steps', 'Step')}
        </Stack>
      </Section>
      <Section title="Membership benefits" blurb="The benefit cards on your home page. They appear when you offer memberships. Change them to match your plans."
        action={<Button size="small" onClick={reset('benefits')}>Use standard wording</Button>}>
        {cardEditor('benefits', 'Benefit')}
      </Section>
      <Section title="Frequently asked questions" blurb="Shown on the FAQ page (the first eight also on the home page). Your list replaces the standard questions."
        action={<Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={reset('faq')}>Use standard questions</Button>
          <Button size="small" startIcon={<AddIcon />} disabled={form.faq.length >= 20} onClick={() => setForm((f) => ({ ...f, faq: [...f.faq, { question: '', answer: '' }] }))}>Add question</Button>
        </Box>}>
        <Stack spacing={2}>
          {form.faq.map((q, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField fullWidth size="small" label="Question" value={q.question} onChange={(e) => setFaq(i, { question: e.target.value })} inputProps={{ maxLength: 200 }} />
                <IconButton size="small" color="error" onClick={() => setForm((f) => ({ ...f, faq: f.faq.filter((_, j) => j !== i) }))}><DeleteIcon /></IconButton>
              </Box>
              <TextField fullWidth size="small" label="Answer" value={q.answer} onChange={(e) => setFaq(i, { answer: e.target.value })} multiline minRows={2} inputProps={{ maxLength: 1200 }} sx={{ mt: 1.5 }} />
            </Paper>
          ))}
          {form.faq.length === 0 && <Typography variant="body2" color="text.secondary">No questions. The standard FAQ will be shown.</Typography>}
        </Stack>
      </Section>
      <SaveBar saving={saving} onSave={save} />
    </>
  );
}

// ---- 3. Services and prices (the Owner Console's own editor, embedded) ---------------------------

export function StepServices({ notify, markDone }) {
  const [unlocked, setUnlocked] = useState(() => !!getOwnerToken());
  const [mountKey, setMountKey] = useState(0); // re-mount the editor after an unlock so it reloads
  useEffect(() => {
    const id = setInterval(() => {
      const t = getOwnerToken();
      const exp = getOwnerExpiresAt();
      setUnlocked(!!t && (!exp || new Date(exp) > new Date()));
    }, 15000);
    return () => clearInterval(id);
  }, []);
  // A stale unlock (from an earlier Owner Console visit) comes back OWNER_LOCKED: ask for the password again
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      if (isOwnerLockedError(error)) setUnlocked(false);
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);
  const onUnlocked = () => { setUnlocked(true); setMountKey((k) => k + 1); };
  const [saving, setSaving] = useState(false);
  const done = async () => {
    setSaving(true);
    try { await markDone('services'); } catch (err) { notify(errorMessage(err, 'Could not save'), 'error'); } finally { setSaving(false); }
  };
  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        These are the services customers can request and what they pay, copied from the platform defaults. Edit names, prices, included miles and per-mile rates, turn services off you do not offer, then click <b>Save Settings</b> on the right and <b>Mark done</b> below. Membership plans are under Owner Console → Customer app → Memberships.
      </Alert>
      <UnlockDialog open={!unlocked} onUnlocked={onUnlocked} />
      {unlocked && <CustomerApp key={mountKey} view="services" />}
      <SaveBar saving={saving} onSave={done} label="Mark done and continue" disabled={!unlocked} />
    </>
  );
}

// ---- 4. Agreements -----------------------------------------------------------------------------

export function StepAgreements({ org, notify, markDone }) {
  const [docs, setDocs] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [active, setActive] = useState('providerAgreement');
  const [editing, setEditing] = useState(null); // { kind, title, body, version }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getDocuments();
      setDocs(data.documents);
      setKinds(data.kinds);
    } catch (err) {
      notify(errorMessage(err, 'Could not load documents'), 'error');
    }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    getDocument(active).then((d) => { if (alive) setEditing({ kind: active, title: d.current?.title || '', body: d.current?.body || '', version: d.current?.version || 0, source: d.current?.source }); })
      .catch((err) => notify(errorMessage(err, 'Could not load the document'), 'error'));
    return () => { alive = false; };
  }, [active, notify]);

  const publish = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const r = await publishDocument(editing.kind, { title: editing.title, body: editing.body });
      notify(r.unchanged ? 'No changes to publish' : `Published version ${r.version}`, 'success');
      await load();
      setEditing((e) => ({ ...e, version: r.version, source: 'owner' }));
    } catch (err) {
      notify(errorMessage(err, 'Could not publish'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const required = ['providerAgreement', 'codeOfConduct', 'privacyPolicy', 'customerTerms'];
  const reviewed = required.every((k) => docs?.[k]?.source === 'owner');
  const done = async () => {
    setSaving(true);
    try { await markDone('agreements'); } catch (err) { notify(errorMessage(err, 'Could not save'), 'error'); } finally { setSaving(false); }
  };
  const publicUrl = (kind) => `${org?.urls?.customerSite ? '' : ''}${API}/api/public/org/documents/${kind}`;

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Each document starts from a template with your company name filled in. Read it, change what you need, and click <b>Publish</b>. Providers accept the Service Provider Agreement and Code of Conduct on your application form; customers see the Privacy Policy, Terms and SMS terms on your website and app. Every publish creates a new version, and applications record the version they accepted. Have your attorney review these before relying on them.
      </Alert>
      <Paper variant="outlined">
        <Tabs value={active} onChange={(e, v) => setActive(v)} variant="scrollable" scrollButtons="auto">
          {kinds.map((k) => (
            <Tab key={k.kind} value={k.kind} label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {k.label}
                {docs?.[k.kind] && <Chip size="small" label={docs[k.kind].source === 'owner' ? `v${docs[k.kind].version}` : 'template'} color={docs[k.kind].source === 'owner' ? 'success' : 'default'} variant="outlined" />}
              </Box>} />
          ))}
        </Tabs>
        <Divider />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {!editing || editing.kind !== active ? <CircularProgress size={24} /> : (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {editing.version ? `Current: version ${editing.version}${editing.source === 'default' ? ' (template, not yet reviewed)' : ''}` : 'Not published yet'}
                </Typography>
                {editing.version > 0 && <Button size="small" endIcon={<OpenIcon />} component={Link} href={publicUrl(active)} target="_blank" rel="noopener">View as customers see it</Button>}
              </Box>
              <TextField label="Title" value={editing.title} onChange={(e) => setEditing((d) => ({ ...d, title: e.target.value }))} inputProps={{ maxLength: 200 }} />
              <TextField label="Document (Markdown: # headings, - lists, **bold**)" value={editing.body} onChange={(e) => setEditing((d) => ({ ...d, body: e.target.value }))} multiline minRows={18} maxRows={40} sx={{ '& textarea': { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 } }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={publish} disabled={saving}>{saving ? 'Publishing…' : `Publish ${editing.version ? `version ${editing.version + 1}` : 'version 1'}`}</Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Paper>
      {!reviewed && docs && <Alert severity="warning" sx={{ mt: 2 }}>Publish your own version of the Service Provider Agreement, Code of Conduct, Privacy Policy and Customer Terms before marking this step done. Opening the template, reading it and clicking Publish is enough if you want to keep the text.</Alert>}
      <SaveBar saving={saving} onSave={done} label="Mark done and continue" disabled={!reviewed} />
    </>
  );
}

// ---- 5. Provider requirements ------------------------------------------------------------------

const INSURANCE = [
  ['generalLiability', 'General liability'], ['autoLiability', 'Auto liability'], ['cargo', 'Cargo'], ['onHook', 'On-hook'], ['garageKeepers', 'Garage keepers']
];
const VEHICLE_OPTIONS = ['Flatbed', 'Wheel-lift', 'Heavy-duty wrecker', 'Rotator', 'Service truck', 'Motorcycle trailer', 'Lowboy'];

export function StepRequirements({ org, reload, notify, markDone }) {
  const [form, setForm] = useState(null);
  const [custom, setCustom] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (org) setForm({ ...org.providerRequirements, insurance: { ...org.providerRequirements.insurance } }); }, [org]);
  if (!form) return <CircularProgress />;
  const save = async () => {
    setSaving(true);
    try {
      await putMe({ providerRequirements: form });
      await reload();
      await markDone('requirements');
    } catch (err) {
      notify(errorMessage(err, 'Could not save'), 'error');
    } finally {
      setSaving(false);
    }
  };
  const toggleVehicle = (v) => setForm((f) => ({ ...f, vehicleTypes: f.vehicleTypes.includes(v) ? f.vehicleTypes.filter((x) => x !== v) : [...f.vehicleTypes, v] }));
  return (
    <>
      <Section title="What you require from service providers" blurb="Shown on your provider application form. Providers upload insurance and documents in the portal after approval, and your team reviews them under Submissions.">
        <TextField fullWidth label="Introduction" value={form.intro} onChange={(e) => setForm((f) => ({ ...f, intro: e.target.value }))} multiline minRows={2} inputProps={{ maxLength: 1000 }} helperText="A short paragraph at the top of the requirements, e.g. who you are looking for and where" sx={{ mb: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Insurance minimums (dollars; 0 = not required)</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {INSURANCE.map(([key, label]) => (
            <Grid item xs={6} md={4} key={key}>
              <TextField fullWidth size="small" type="number" label={label} value={form.insurance[key] ?? 0} onChange={(e) => setForm((f) => ({ ...f, insurance: { ...f.insurance, [key]: e.target.value } }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
          ))}
        </Grid>
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
          <FormControlLabel control={<Checkbox checked={!!form.backgroundCheck} onChange={(e) => setForm((f) => ({ ...f, backgroundCheck: e.target.checked }))} />} label="Driver background checks" />
          <FormControlLabel control={<Checkbox checked={!!form.w9} onChange={(e) => setForm((f) => ({ ...f, w9: e.target.checked }))} />} label="W-9 on file" />
          <FormControlLabel control={<Checkbox checked={!!form.businessLicense} onChange={(e) => setForm((f) => ({ ...f, businessLicense: e.target.checked }))} />} label="Business license" />
        </Stack>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Equipment we dispatch to</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {VEHICLE_OPTIONS.map((v) => <Chip key={v} label={v} clickable color={form.vehicleTypes.includes(v) ? 'primary' : 'default'} variant={form.vehicleTypes.includes(v) ? 'filled' : 'outlined'} onClick={() => toggleVehicle(v)} />)}
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Other requirements</Typography>
        <List dense disablePadding>
          {form.custom.map((c, i) => (
            <ListItem key={i} secondaryAction={<IconButton edge="end" size="small" onClick={() => setForm((f) => ({ ...f, custom: f.custom.filter((_, j) => j !== i) }))}><DeleteIcon fontSize="small" /></IconButton>}>
              <ListItemText primary={c} />
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" fullWidth placeholder="e.g. 24/7 availability in your coverage area" value={custom} onChange={(e) => setCustom(e.target.value)} inputProps={{ maxLength: 200 }}
            onKeyDown={(e) => { if (e.key === 'Enter' && custom.trim()) { e.preventDefault(); setForm((f) => ({ ...f, custom: [...f.custom, custom.trim()] })); setCustom(''); } }} />
          <Button startIcon={<AddIcon />} disabled={!custom.trim() || form.custom.length >= 20} onClick={() => { setForm((f) => ({ ...f, custom: [...f.custom, custom.trim()] })); setCustom(''); }}>Add</Button>
        </Box>
      </Section>
      <SaveBar saving={saving} onSave={save} />
    </>
  );
}

// ---- 6. Social links and apps ------------------------------------------------------------------

const SOCIAL = [['facebook', 'Facebook'], ['instagram', 'Instagram'], ['x', 'X (Twitter)'], ['tiktok', 'TikTok'], ['youtube', 'YouTube'], ['google', 'Google Business profile']];

export function StepSocialApps({ org, reload, notify, markDone }) {
  const [social, setSocial] = useState({});
  const [apps, setApps] = useState({ ios: '', android: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (org) { setSocial({ ...org.branding.social }); setApps({ ios: org.branding.appLinks?.ios || '', android: org.branding.appLinks?.android || '' }); } }, [org]);
  const save = async () => {
    setSaving(true);
    try {
      await putMe({ branding: { social, appLinks: apps } });
      await reload();
      await markDone('social');
    } catch (err) {
      notify(errorMessage(err, 'Could not save'), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <Section title="Social links" blurb="Shown in your customer website's footer. Leave blank any you do not use.">
        <Grid container spacing={2}>
          {SOCIAL.map(([key, label]) => (
            <Grid item xs={12} md={6} key={key}><TextField fullWidth size="small" label={label} placeholder="https://…" value={social[key] || ''} onChange={(e) => setSocial((s) => ({ ...s, [key]: e.target.value }))} /></Grid>
          ))}
        </Grid>
      </Section>
      <Section title="Mobile apps" blurb="Your customers and drivers use the partner edition of the platform's apps and see your branding after they sign in. Leave these blank to use the standard store links; fill them in only if you publish your own apps.">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><TextField fullWidth size="small" label="App Store link (iOS)" placeholder="https://apps.apple.com/…" value={apps.ios} onChange={(e) => setApps((a) => ({ ...a, ios: e.target.value }))} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Google Play link (Android)" placeholder="https://play.google.com/…" value={apps.android} onChange={(e) => setApps((a) => ({ ...a, android: e.target.value }))} /></Grid>
        </Grid>
      </Section>
      <SaveBar saving={saving} onSave={save} />
    </>
  );
}

// ---- 7. Payments -------------------------------------------------------------------------------

export function StepPayments({ org, reload, notify, markDone }) {
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [stripe, setStripe] = useState(null); // from GET /api/org/me/stripe
  const [error, setError] = useState('');
  const returned = typeof window !== 'undefined' && /[?&]stripe=(return|refresh)/.test(window.location.search);

  const loadStripe = useCallback(async () => {
    try { setStripe(await getStripe()); setError(''); } catch (err) { setError(errorMessage(err, 'Could not load the Stripe status')); }
  }, []);
  useEffect(() => { loadStripe(); }, [loadStripe]);

  const done = async () => {
    setSaving(true);
    try { await markDone('payments'); } catch (err) { notify(errorMessage(err, 'Could not save'), 'error'); } finally { setSaving(false); }
  };
  const connect = async () => {
    setBusy('connect');
    try {
      const { url } = await startStripeOnboarding();
      window.location.assign(url); // Stripe's hosted onboarding; it returns to this step
    } catch (err) {
      notify(errorMessage(err, 'Could not start Stripe onboarding'), 'error');
      setBusy('');
    }
  };
  const goLive = async () => {
    setBusy('live');
    try { setStripe(await stripeGoLive()); await reload(); notify('Live payments are on', 'success'); } catch (err) { notify(errorMessage(err, 'Could not go live'), 'error'); } finally { setBusy(''); }
  };
  const backToDemo = async () => {
    setBusy('demo');
    try { setStripe(await stripeBackToDemo()); await reload(); notify('Demo mode is on', 'success'); } catch (err) { notify(errorMessage(err, 'Could not switch to demo'), 'error'); } finally { setBusy(''); }
  };

  const state = !stripe ? 'loading' : stripe.mode === 'live' ? 'live' : stripe.connected ? (stripe.chargesEnabled ? 'ready' : 'onboarding') : 'demo';
  return (
    <>
      <Section title="Payments" blurb="How customers pay for service and memberships. Your company starts in demo mode and goes live on your own Stripe account.">
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {state === 'loading' && <CircularProgress size={22} />}
        {state === 'demo' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <b>Demo mode is on.</b> Customer payments are simulated: requests, dispatch, receipts and payouts all work, but no card is charged and every receipt is marked Demo. Use it to learn the platform and train your team. When you are ready to take real payments, connect your Stripe account below.
          </Alert>
        )}
        {state === 'onboarding' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <b>Stripe onboarding started.</b> {returned ? 'Welcome back. ' : ''}Stripe is still verifying your account{stripe.detailsSubmitted ? '' : ', or needs more information from you'}. Payments stay in demo mode until Stripe has card payments active. You can re-open Stripe to finish any remaining steps.
          </Alert>
        )}
        {state === 'ready' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <b>Your Stripe account is verified.</b> Customer payments are still in demo mode. Go live when your services, prices and agreements are ready: from then on customers are charged on your Stripe account and paid out by Stripe to your bank.
          </Alert>
        )}
        {state === 'live' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <b>Live payments are on.</b> Customers are charged on your own Stripe account; Stripe pays out to your bank on its schedule. {stripe.platformFeePct > 0 ? `${stripe.platformFeePct}% of each card payment goes to the platform as its fee.` : ''}
          </Alert>
        )}
        {stripe && !stripe.platformConfigured && <Alert severity="warning" sx={{ mb: 2 }}>Stripe is not configured on the platform yet, so connecting is not possible right now. Demo mode keeps working.</Alert>}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {(state === 'demo' || state === 'onboarding') && (
            <Button variant="contained" onClick={connect} disabled={!!busy || !stripe?.platformConfigured} startIcon={<OpenIcon />}>
              {busy === 'connect' ? 'Opening Stripe…' : state === 'demo' ? 'Connect Stripe' : 'Continue in Stripe'}
            </Button>
          )}
          {state === 'ready' && <Button variant="contained" color="success" onClick={goLive} disabled={!!busy}>{busy === 'live' ? 'Switching…' : 'Go live'}</Button>}
          {state === 'live' && <Button variant="outlined" onClick={backToDemo} disabled={!!busy}>{busy === 'demo' ? 'Switching…' : 'Back to demo mode'}</Button>}
          {stripe?.connected && <Button onClick={loadStripe} disabled={!!busy}>Refresh status</Button>}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Providers are paid weekly by ACH from the payouts you run under Payments. Payout day and time are set under Owner Console → Payouts.
        </Typography>
      </Section>
      <SaveBar saving={saving} onSave={done} label="Continue" />
    </>
  );
}

// ---- 8. Team -----------------------------------------------------------------------------------

export function StepTeam({ notify, markDone }) {
  const { currentUser } = useUser();
  const [team, setTeam] = useState(null);
  const [regions, setRegions] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([getTeam(), axios.get(`${API}/api/regions`).then((x) => x.data).catch(() => [])]);
      setTeam(t);
      setRegions(Array.isArray(r) ? r : []);
    } catch (err) {
      notify(errorMessage(err, 'Could not load your team'), 'error');
    }
  }, [notify]);
  useEffect(() => { load(); }, [load]);
  const availableRoles = useMemo(() => ['sOW', 'RM', 'SP'], []);
  const done = async () => {
    setSaving(true);
    try { await markDone('team'); } catch (err) { notify(errorMessage(err, 'Could not save'), 'error'); } finally { setSaving(false); }
  };
  const roleLabel = { OWp: 'Owner', OW: 'Owner', sOW: 'Co-owner', RM: 'Regional manager', SP: 'Service provider' };
  return (
    <>
      <Section title="Your team" blurb="Add co-owners and regional managers now or later under Settings → Users. Service providers apply through your application form and are approved under Submissions."
        action={<Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add team member</Button>}>
        {!team ? <CircularProgress size={24} /> : (
          <List dense disablePadding>
            {team.map((u) => (
              <ListItem key={u.id} divider>
                <ListItemText primary={`${u.name || u.username}${u.id === currentUser?._id ? ' (you)' : ''}`} secondary={`${roleLabel[u.role] || u.role} · ${u.email}${u.active ? '' : ' · inactive'}`} />
              </ListItem>
            ))}
          </List>
        )}
      </Section>
      {open && (
        <CreateUserDialog open={open} onClose={() => setOpen(false)} currentUser={currentUser} availableRoles={availableRoles} regions={regions}
          onUserCreated={() => { notify('Team member added', 'success'); load(); }} />
      )}
      <SaveBar saving={saving} onSave={done} label="Continue" />
    </>
  );
}

// ---- 9. Finish ---------------------------------------------------------------------------------

export function StepFinish({ org, completing, onComplete, missing }) {
  const links = [
    ['Portal (your team and providers sign in here)', org?.urls?.portal],
    ['Customer website', org?.urls?.customerSite],
    ['Provider application form', org?.urls?.applicationForm],
    ['Help and tutorials', `${org?.urls?.portal || ''}/help`]
  ];
  const stepLabel = { branding: 'Branding', services: 'Services & prices', agreements: 'Agreements', requirements: 'Provider requirements' };
  return (
    <>
      {org?.setup?.completedAt ? (
        <Alert severity="success" sx={{ mb: 2 }}>Setup is complete. You can come back to any step from Owner Console → Company → Company setup.</Alert>
      ) : missing?.length ? (
        <Alert severity="warning" sx={{ mb: 2 }}>Before finishing, complete: {missing.map((m) => stepLabel[m] || m).join(', ')}{!org?.branding?.logoUrl ? ' (and upload your logo)' : ''}.</Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>Everything required is done. Finishing setup makes your company active: your website and application form go live at the addresses below.</Alert>
      )}
      <Section title="Your addresses">
        <List dense disablePadding>
          {links.map(([label, url]) => (
            <ListItem key={label} divider>
              <ListItemText primary={label} secondary={url ? <Link href={url} target="_blank" rel="noopener">{url}</Link> : '—'} />
            </ListItem>
          ))}
        </List>
      </Section>
      <Section title="What happens next">
        <Typography variant="body2" paragraph>Share the application form link with towing and service companies you work with. When they apply, you will see them under <b>Submissions</b> and get an email. Approve them, send their rate agreement, and they can start taking jobs.</Typography>
        <Typography variant="body2" paragraph>Customers request service on your website or in the app. Requests appear on your <b>Dashboard</b>, offers go to your providers' drivers, and you can follow every job live.</Typography>
        <Typography variant="body2">Every setting here can be changed later under <b>Owner Console</b>.</Typography>
      </Section>
      {!org?.setup?.completedAt && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" size="large" onClick={onComplete} disabled={completing || (missing?.length > 0)}>{completing ? 'Finishing…' : 'Finish setup'}</Button>
        </Box>
      )}
    </>
  );
}

export const STEP_COMPONENTS = {
  branding: StepBranding, about: StepAbout, sitePages: StepSitePages, services: StepServices, agreements: StepAgreements,
  requirements: StepRequirements, social: StepSocialApps, payments: StepPayments, team: StepTeam
};

export const STEP_META = [
  { key: 'branding', label: 'Branding', required: true },
  { key: 'about', label: 'About & reviews' },
  { key: 'sitePages', label: 'Edit site pages' },
  { key: 'services', label: 'Services & prices', required: true },
  { key: 'agreements', label: 'Agreements', required: true },
  { key: 'requirements', label: 'Provider requirements', required: true },
  { key: 'social', label: 'Social & apps' },
  { key: 'payments', label: 'Payments' },
  { key: 'team', label: 'Team' },
  { key: 'finish', label: 'Finish' }
];

export { MenuItem };
